/**
 * The menu look (docs/spec/04 §1, §6): the grid, colours and clocks every screen shares, so they
 * all read as one cabinet. Colours are palette indices. Pure.
 */

import {
  BLUE, CYAN, DARK_GREY, GREY, LIME, MAGENTA, ORANGE, PINK, RED, SKY, WHITE, YELLOW,
} from '../../render/palette';
import { FORMATION, TICKS_PER_SECOND } from '../../sim/tuning';

/** The virtual screen: 28 columns by 36 rows of 8 x 8 text. */
export const W = 224;
export const H = 288;
export const CX = W / 2;
export const CELL = 8;
/** Text keeps to columns 1-26, a column clear of either edge. */
export const LEFT = CELL;
export const RIGHT = W - CELL;
export const TEXT_COLS = 26;

/** Plain text. */
export const INK = WHITE;
/** The selected item. */
export const SELECTED = YELLOW;
/** Secondary text: hints, labels. */
export const MUTED = GREY;
/** Tertiary: frames, disabled items. */
export const FAINT = DARK_GREY;
/** Headings, in the red of the cabinet's HIGH SCORE. */
export const HEADING = RED;
/** Values and highlights. */
export const ACCENT = CYAN;
export const GOOD = LIME;
export const BAD = ORANGE;

/** Colour ramps, dark to bright and back, for rules and lights. */
export const RAMPS = {
  fire: [RED, ORANGE, YELLOW, WHITE, YELLOW, ORANGE],
  ice: [BLUE, SKY, CYAN, WHITE, CYAN, SKY],
} as const satisfies Record<string, readonly number[]>;

/** A bright cycle for a new high score and other celebrations. */
export const RAINBOW: readonly number[] = [YELLOW, WHITE, CYAN, SKY, MAGENTA, PINK, ORANGE, LIME];

/** The y of character row `n`. */
export function row(n: number): number {
  return n * CELL;
}

let calm = false;

/** Reduce flashing (04 §6): colour cycles and blinks slow to 2 Hz or less. Set once a frame. */
export function setCalm(on: boolean): void {
  calm = on;
}

export function isCalm(): boolean {
  return calm;
}

/** How long a colour-cycle step lasts, given the wanted `stepMs` and the Reduce flashing setting. */
export function calmStep(stepMs: number): number {
  return calm ? Math.max(stepMs, 500) : stepMs;
}

/** On for the first half of every `period` ms (never faster than once a second when calm). */
export function blink(ms: number, period = 800): boolean {
  const p = calm ? Math.max(period, 1000) : period;
  return Math.floor(ms / (p / 2)) % 2 === 0;
}

/** A colour from a cycle, stepping every `stepMs`. */
export function cycle(colours: readonly number[], ms: number, stepMs = 80, offset = 0): number {
  const n = colours.length;
  const i = (((Math.floor(ms / calmStep(stepMs)) + offset) % n) + n) % n;
  return colours[i] ?? WHITE;
}

/** Smooth 0..1 for a transition that started `ms` ago and lasts `total` ms. */
export function ease(ms: number, total: number): number {
  const p = Math.min(1, Math.max(0, ms / total));
  return 1 - (1 - p) * (1 - p) * (1 - p);
}

/** The `t` to hand `drawCapsule` at `ms`: game ticks, or a slow pulse when flashing is reduced. */
export function capsuleTicks(ms: number): number {
  return calm ? Math.floor(ms / 1000) * 8 : Math.floor((ms * TICKS_PER_SECOND) / 1000);
}

const BEAT_MS = (FORMATION.beat * 1000) / TICKS_PER_SECOND;

/** The wing beat the hive shares (02 §3): which of an enemy's two frames to show at `ms`. */
export function wingFrame(ms: number): number {
  return Math.floor(Math.max(0, ms) / BEAT_MS) % 2;
}
